import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Head from 'next/head';
import { lazy, ReactNode, Suspense, useEffect } from 'react';

import {
	getCityzensProfile,
	GetCityzensProfileError,
	putProfileAvatar,
} from 'services/account';
import {
	getFooter,
	getMainNav,
	getPartnerLogos,
} from 'services/mancity-template';

import { useBreadcrumbs } from 'hooks/use-breadcrumbs';
import { useBrowsing } from 'hooks/use-browsing';
import { useErrorHandler } from 'hooks/use-error-handler';
import { useRedirect } from 'hooks/use-redirect';
import { useToast } from 'hooks/use-toast';

import { useUserStore } from 'stores/use-user-store';

import {
	PUBLIC_FF_HEADER_BASKET_ICON,
	PUBLIC_GTM_ID_ENV,
	PUBLIC_STORE_PAGE_TITLE_ENV,
} from 'tools/environments';

import { featuresList } from '../data/features-list';
import styles from './layout.module.scss';

const ManCityTemplate = lazy(() => import('ui/mancity/template'));
const Breadcrumbs = lazy(
	() => import('core/generic/components/navigation/breadcrumbs')
);
const Button = lazy(() => import('core/generic/elements/button'));
const Profile = lazy(() => import('@myaccount/components/profile/profile'));
const PartialHeader = lazy(
	() => import('@myaccount/components/partial-header/partial-header')
);

type LayoutProps = {
	children: ReactNode;
};

const analyticsConfig = {
	gtmId: PUBLIC_GTM_ID_ENV,
};

const HELP_CENTRE_ROUTES = new Set([
	'/',
	'/orders/[order]',
	'/orders/[order]/[alias]',
]);

export const Layout = ({ children }: LayoutProps) => {
	const toast = useToast();
	const handleError = useErrorHandler();
	const redirect = useRedirect();
	const { generateNextBreadcrumbs, generateCustomBreadcrumbs } =
		useBreadcrumbs();
	const { pathname, query, events } = useBrowsing();
	const isFromApp = query.fromapp !== undefined ? true : false;
	const isPartialPage = pathname === '/preferences/partial';
	const isHome = pathname === '/';
	const showHelpCentre = HELP_CENTRE_ROUTES.has(pathname);
	const isGuest = /^\/orders\/find-my-order/.test(pathname);
	const isFindMyOrder = pathname === '/orders/find-my-order';
	const hideTemplateRoutesArray = [
		'/preferences/partial',
		'/orders/find-my-order',
		'/orders/find-my-order/[order]',
		'/orders/find-my-order/[order]/[alias]',
	];
	const breadcrumbs =
		generateCustomBreadcrumbs({ baseName: isGuest ? null : 'My Account' })
			.length > 1
			? generateCustomBreadcrumbs({
					baseName: isGuest ? null : 'My Account',
				})
			: generateNextBreadcrumbs({
					baseName: isGuest ? null : 'My Account',
				});

	const queryClient = useQueryClient();

	useEffect(() => {
		const handleRouteChange = () => {
			document.getElementById('focus-sentinel')?.focus();
		};

		events?.on('routeChangeComplete', handleRouteChange);
		return () => {
			events?.off('routeChangeComplete', handleRouteChange);
		};
	}, [events]);

	const { setUser } = useUserStore((state) => ({
		setUser: state.setUser,
	}));

	const getProfile = async () => {
		try {
			const data = await getCityzensProfile();

			if (data) {
				setUser(data);
				return data;
			}

			if (isPartialPage || isGuest) {
				return undefined;
			}

			const error = new Error('Unauthorized') as GetCityzensProfileError;
			error.status = 401;
			handleError.process(error, true, error.status);
			return undefined;
		} catch (error) {
			if (isPartialPage || isGuest) {
				return undefined;
			}

			handleError.process(
				error as GetCityzensProfileError,
				true,
				(error as GetCityzensProfileError).status
			);
		}
	};

	const profileQuery = useQuery({
		queryKey: ['profile-cityzens'],
		queryFn: getProfile,
		retry: false,
		throwOnError: false,
	});

	const footerQuery = useQuery({
		queryKey: ['footer'],
		queryFn: () => getFooter().then((response) => response.data),
		enabled: !(isFromApp || isPartialPage || isGuest),
	});

	const mainNavQuery = useQuery({
		queryKey: ['main-nav'],
		queryFn: () => getMainNav().then((response) => response.data),
		enabled: !(isFromApp || isPartialPage || isGuest),
	});

	const partnerLogosQuery = useQuery({
		queryKey: ['partners'],
		queryFn: () => getPartnerLogos().then((response) => response.data),
		enabled: !(isFromApp || isPartialPage || isGuest),
	});

	const profileImageMutation = useMutation({
		mutationFn: (profileImage: string) =>
			putProfileAvatar({ profileImage }).then(() => {
				toast.success({
					content: 'Profile image updated successfully',
				});
			}),
		onSuccess: () => {
			profileQuery.refetch();
		},
	});

	const navItems =
		mainNavQuery.data?.find(({ navgroup }) => navgroup === 'primary')
			?.items || [];

	const getTitle = () => {
		return (
			featuresList.find(({ url }) => url === pathname)?.title ?? 'Account'
		);
	};

	if (profileQuery.isError && !isPartialPage && !isGuest) {
		if (profileQuery.isStale) {
			redirect({ code: 401 });
		}

		return <div>Redirecting...</div>;
	}

	if (profileQuery.isFetched) {
		return (
			<Suspense fallback={null}>
				<>
					<Head>
						<title>{`${getTitle()} - ${PUBLIC_STORE_PAGE_TITLE_ENV}`}</title>
					</Head>

					<ManCityTemplate
						headerNavigationData={navItems}
						partnersData={partnerLogosQuery.data}
						footerData={footerQuery.data}
						hideTemplateRoutes={hideTemplateRoutesArray}
						config={analyticsConfig}
						showBasketIcon={PUBLIC_FF_HEADER_BASKET_ICON}
					>
						{isPartialPage || isGuest ? <PartialHeader /> : null}

						<div className={styles.container}>
							<div className={styles.main} role="main" id="main">
								<div className={`${styles.grid} container`}>
									{isHome ? (
										<div className="row">
											<div className="col-12">
												<div className={styles.profile}>
													<Profile
														isLoading={
															profileImageMutation.isPending ||
															profileQuery.isRefetching
														}
														onImageChange={
															profileImageMutation.mutate
														}
														onDeleteImage={
															profileImageMutation.mutate
														}
													/>
												</div>
											</div>
										</div>
									) : null}

									<div className="row">
										<div className="col-12">
											{isHome ||
											isPartialPage ||
											isFromApp ||
											isFindMyOrder ? null : (
												<div
													className={
														styles.breadcrumbs
													}
												>
													<Breadcrumbs
														mobileLayout="scroll"
														listCrumbs={breadcrumbs}
													/>
												</div>
											)}

											{children}
										</div>
									</div>
								</div>
							</div>

							<div
								id="box-submit"
								className={styles['box-submit']}
							></div>
						</div>

						{showHelpCentre && (
							<div className={styles['help-centre']}>
								<div
									className={`${styles['help-centre__wrapper']} container`}
								>
									<div className="row">
										<div
											className={`${styles['help-centre__content']} col-12`}
										>
											<div>
												<h2 className="display-small bold">
													How can we help you?
												</h2>
												<p className="body-medium">
													Find answers in our Help
													Centre or contact support
													for assistance.
												</p>
											</div>
											<span
												className={
													styles[
														'help-centre__button'
													]
												}
											>
												<Button
													label="Visit Help Centre"
													href="https://www.mancity.com/help"
													target="_blank"
													dimension="medium"
													fullWidth
												/>
											</span>
										</div>
									</div>
								</div>
							</div>
						)}
					</ManCityTemplate>
				</>
			</Suspense>
		);
	}
};
